import { NavLink } from 'react-router-dom';
import { clsx } from 'clsx';
import {
  HomeIcon,
  PlusCircleIcon,
  ClipboardDocumentListIcon,
  UsersIcon,
  CubeIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
  UserGroupIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  CreditCardIcon,
  BanknotesIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  QueueListIcon,
  CalculatorIcon,
} from '@heroicons/react/24/outline';
import { useUIStore } from '@/stores/uiStore';
import { useTenantStore, FEATURES } from '@/stores/tenantStore';

const navigation = [
  { name: 'לוח בקרה', href: '/dashboard', icon: HomeIcon },
  { name: 'קליטה חדשה', href: '/intake', icon: PlusCircleIcon },
  { name: 'טיקטים', href: '/tickets', icon: ClipboardDocumentListIcon },
  { name: 'תור עבודה', href: '/queue', icon: QueueListIcon },
  { name: 'לקוחות', href: '/customers', icon: UsersIcon },
  { name: 'טכנאים', href: '/technicians', icon: WrenchScrewdriverIcon },
  { name: 'מלאי', href: '/inventory', icon: CubeIcon },
  { name: 'מכירות', href: '/sales', icon: CurrencyDollarIcon },
  { name: 'חשבוניות', href: '/invoices', icon: DocumentTextIcon },
  { name: 'תשלומים', href: '/payments', icon: CreditCardIcon },
  { name: 'עמלות', href: '/commissions', icon: BanknotesIcon },
  { name: 'קופה', href: '/cash', icon: CalculatorIcon },
  { name: 'דוחות', href: '/reports', icon: ChartBarIcon },
  { name: 'משתמשים', href: '/users', icon: UserGroupIcon },
  { name: 'הגדרות', href: '/settings', icon: Cog6ToothIcon },
];

export function Sidebar() {
  const { sidebarOpen, sidebarCollapsed, setSidebarCollapsed } = useUIStore();
  const { hasFeature, tenantInfo } = useTenantStore();

  if (!sidebarOpen) return null;

  return (
    <aside
      className={clsx(
        'fixed inset-y-0 right-0 z-40 flex flex-col bg-gradient-sidebar transition-all duration-400',
        sidebarCollapsed ? 'w-70' : 'w-260'
      )}
    >
      {/* Animated gradient bar at top */}
      <div className="header-gradient-bar" />

      {/* Logo */}
      <div className={clsx(
        'flex items-center h-16 px-4 border-b border-white/10',
        sidebarCollapsed ? 'justify-center' : 'justify-between'
      )}>
        {!sidebarCollapsed && (
          <div className="flex items-center gap-3 animate-fade-in">
            <div className="w-10 h-10 bg-gradient-primary rounded-levor flex items-center justify-center shadow-glow-blue">
              <span className="text-white font-bold text-xl">T</span>
            </div>
            <div>
              <span className="font-bold text-lg text-white">
                {tenantInfo?.name || 'TCY Repair'}
              </span>
              <div className="text-xs text-white/50">מערכת ניהול תיקונים</div>
            </div>
          </div>
        )}
        {sidebarCollapsed && (
          <div className="w-10 h-10 bg-gradient-primary rounded-levor flex items-center justify-center shadow-glow-blue animate-pulse-slow">
            <span className="text-white font-bold text-xl">T</span>
          </div>
        )}
      </div>

      {/* Toggle button - RTL: on left side of sidebar */}
      <button
        onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
        className={clsx(
          'absolute top-20 -left-3 bg-gradient-primary rounded-full p-1.5 shadow-glow-blue',
          'hover:shadow-glow-blue-lg hover:scale-110 transition-all duration-300'
        )}
        aria-label={sidebarCollapsed ? 'הרחב תפריט צד' : 'כווץ תפריט צד'}
      >
        {/* RTL: ChevronRight expands (towards left/content), ChevronLeft collapses (towards right/sidebar) */}
        {sidebarCollapsed ? (
          <ChevronLeftIcon className="w-4 h-4 text-white" />
        ) : (
          <ChevronRightIcon className="w-4 h-4 text-white" />
        )}
      </button>

      {/* Navigation */}
      <nav className={clsx(
        'flex-1 py-4 overflow-y-auto overflow-x-hidden',
        sidebarCollapsed ? 'px-3' : 'px-4'
      )}>
        <div className={clsx(
          'space-y-2',
          sidebarCollapsed && 'flex flex-col items-center'
        )}>
          {navigation.map((item, index) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                clsx(
                  'group relative flex items-center transition-all duration-300',
                  sidebarCollapsed ? 'sidebar-icon-btn' : 'sidebar-link',
                  isActive && (sidebarCollapsed ? 'active' : 'active')
                )
              }
              style={{ animationDelay: `${index * 50}ms` }}
              aria-label={sidebarCollapsed ? item.name : undefined}
            >
              {({ isActive }) => (
                <>
                  <item.icon className={clsx(
                    'flex-shrink-0 transition-all duration-300',
                    sidebarCollapsed ? 'w-5 h-5' : 'w-5 h-5',
                    isActive ? 'text-white' : 'text-white/70 group-hover:text-white'
                  )} />

                  {!sidebarCollapsed && (
                    <span className="ms-3">{item.name}</span>
                  )}

                  {/* Tooltip for collapsed state - RTL: appears on left */}
                  {sidebarCollapsed && (
                    <div className={clsx(
                      'absolute left-full me-3 px-3 py-2 bg-sidebar rounded-levor shadow-levor',
                      'text-white text-sm whitespace-nowrap opacity-0 invisible',
                      'group-hover:opacity-100 group-hover:visible transition-all duration-200',
                      'pointer-events-none z-50'
                    )}>
                      {item.name}
                      {/* Arrow - RTL: points right towards sidebar */}
                      <div className="absolute right-full top-1/2 -translate-y-1/2 border-8 border-transparent border-l-sidebar"
                           style={{ borderLeftColor: '#011939' }} />
                    </div>
                  )}

                  {/* Active indicator bar */}
                  {isActive && !sidebarCollapsed && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-3/4 bg-gradient-primary rounded-l-full" />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </div>

        {/* AI Features section */}
        {(hasFeature(FEATURES.AI_DIAGNOSIS) || hasFeature(FEATURES.AI_QUICK_INTAKE)) && (
          <div className={clsx('mt-6 pt-6 border-t border-white/10', sidebarCollapsed && 'flex flex-col items-center')}>
            {!sidebarCollapsed && (
              <div className="px-3 mb-3 text-xs font-semibold text-white/40 uppercase tracking-wider">
                AI Tools
              </div>
            )}
            {hasFeature(FEATURES.AI_QUICK_INTAKE) && (
              <NavLink
                to="/ai/quick-intake"
                className={({ isActive }) =>
                  clsx(
                    'group relative flex items-center transition-all duration-300',
                    sidebarCollapsed ? 'sidebar-icon-btn mt-2' : 'sidebar-link',
                    isActive && 'active'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <SparklesIcon className={clsx(
                      'w-5 h-5 flex-shrink-0 transition-all duration-300',
                      isActive ? 'text-purple-300' : 'text-purple-400 group-hover:text-purple-300'
                    )} />
                    {!sidebarCollapsed && <span className="ms-3">קליטה מהירה AI</span>}

                    {/* Tooltip - RTL: appears on left */}
                    {sidebarCollapsed && (
                      <div className="absolute left-full me-3 px-3 py-2 bg-sidebar rounded-levor shadow-levor text-white text-sm whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50">
                        קליטה מהירה AI
                      </div>
                    )}
                  </>
                )}
              </NavLink>
            )}
            {hasFeature(FEATURES.AI_DIAGNOSIS) && (
              <NavLink
                to="/ai/diagnosis"
                className={({ isActive }) =>
                  clsx(
                    'group relative flex items-center transition-all duration-300',
                    sidebarCollapsed ? 'sidebar-icon-btn mt-2' : 'sidebar-link',
                    isActive && 'active'
                  )
                }
              >
                {({ isActive }) => (
                  <>
                    <SparklesIcon className={clsx(
                      'w-5 h-5 flex-shrink-0 transition-all duration-300',
                      isActive ? 'text-purple-300' : 'text-purple-400 group-hover:text-purple-300'
                    )} />
                    {!sidebarCollapsed && <span className="ms-3">אבחון AI</span>}

                    {/* Tooltip - RTL: appears on left */}
                    {sidebarCollapsed && (
                      <div className="absolute left-full me-3 px-3 py-2 bg-sidebar rounded-levor shadow-levor text-white text-sm whitespace-nowrap opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 pointer-events-none z-50">
                        אבחון AI
                      </div>
                    )}
                  </>
                )}
              </NavLink>
            )}
          </div>
        )}
      </nav>

      {/* Footer */}
      <div className={clsx(
        'p-4 border-t border-white/10',
        sidebarCollapsed && 'flex justify-center'
      )}>
        {!sidebarCollapsed ? (
          <div className="text-xs text-white/30 text-center">
            <div>TCY Repair System</div>
            <div className="mt-1">v1.0</div>
          </div>
        ) : (
          <div className="w-2 h-2 rounded-full bg-gradient-primary animate-pulse-slow" />
        )}
      </div>
    </aside>
  );
}
